/**
 * A scannable QR code, as inline SVG.
 *
 * WHY THIS EXISTS AT ALL. The pairing screen used to render the WhatsApp
 * payload as its raw TEXT and ask the operator to paste it into some other QR
 * renderer — recorded in ui.md as a gap since Step 5, because drawing one
 * needs an encoder and the brief said to ask before adding a dependency. It is
 * the first thing anybody does with the WhatsApp feature and the one screen
 * that has to work on the first try, so it is now drawn here.
 *
 * `qrcode-generator` does the encoding: Reed-Solomon, mask selection and
 * version choice are genuinely subtle, and a hand-rolled encoder whose failure
 * mode is «scans on my phone, not on yours» is the worst possible thing to
 * home-grow. It has ZERO transitive dependencies and returns a module matrix
 * rather than an image, so the drawing stays here and stays on design tokens.
 *
 * SVG rather than a canvas: it scales to any density without going soft, it
 * needs no ref or effect, and it renders identically in jsdom — so the test
 * can count modules instead of asserting a component was mounted.
 */
import qrcode from 'qrcode-generator';
import React, { useMemo } from 'react';

/** Error correction L, and that is a scanning decision rather than a lazy one.
 *  Higher levels add modules; at a fixed on-screen size more modules means
 *  SMALLER ones, which is the thing a phone camera actually struggles with.
 *  A pairing QR lives ~20 seconds on a clean screen — it is not a sticker on a
 *  van, and it does not need to survive being scratched. */
const ERROR_CORRECTION = 'L';

/** The quiet zone, in modules. FOUR is what the QR spec requires and what
 *  decoders assume; a QR flush against a card border is a QR that intermittently
 *  fails to be found at all, which reads to the user as a broken feature. */
const QUIET_ZONE = 4;

/**
 * Build one SVG path covering every dark module.
 *
 * ONE path, not one <rect> per module: a WhatsApp payload needs roughly 49–61
 * modules a side, so the rect version is up to ~1800 elements re-created every
 * time the code rotates — every twenty seconds, for as long as the operator
 * takes to scan.
 */
function modulePath(qr, count) {
  const parts = [];
  for (let row = 0; row < count; row += 1) {
    for (let column = 0; column < count; column += 1) {
      if (qr.isDark(row, column)) {
        // One unit square per module; the viewBox does the scaling.
        parts.push(`M${column + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`);
      }
    }
  }
  return parts.join('');
}

export function QrCode({ value, label }) {
  const drawn = useMemo(() => {
    if (!value) return null;
    try {
      // Type 0 = pick the smallest version that fits the payload. A pinned
      // version would refuse a longer-than-expected payload outright, and
      // WhatsApp does not promise the length of its reference string.
      const qr = qrcode(0, ERROR_CORRECTION);
      qr.addData(value);
      qr.make();
      const count = qr.getModuleCount();
      return { count, path: modulePath(qr, count) };
    } catch {
      // An unencodable payload must not take the screen down with it — the
      // caller still renders the text fallback underneath, which is a worse
      // experience and not a broken one.
      return null;
    }
  }, [value]);

  if (!drawn) return null;

  const side = drawn.count + QUIET_ZONE * 2;
  return (
    <svg
      // A QR is never mirrored, so it opts out of the RTL document direction.
      dir="ltr"
      className="h-qr w-qr rounded border border-border bg-qr-quiet"
      viewBox={`0 0 ${side} ${side}`}
      role="img"
      aria-label={label}
      // Without this the browser antialiases module edges and a marginal scan
      // gets more marginal.
      shapeRendering="crispEdges"
    >
      <rect width={side} height={side} className="fill-qr-quiet" />
      <path d={drawn.path} className="fill-qr-module" />
    </svg>
  );
}
